/**
 * AnesthesiaSheetDialog — Hoja de anestesia DIGITAL (2026-07-17).
 *
 * Réplica rellenable del impreso "REGISTRO DE QUIROFANO" que el anestesista
 * completaba en papel: cabecera del paciente, gráfica por horas (constantes y
 * medicación), datos de anestesia y salida de quirófano. Se autoguarda por
 * paciente + fecha de cirugía (una hoja por paciente y día) y queda registrado.
 *
 * Cuadro de firma (administración, 2026-09-09): junto a la firma —tanto la de la
 * HOJA como la del CONSENTIMIENTO de anestesia— hay el MISMO desplegable de
 * anestesistas dados de alta (<SelectorAnestesista>, Catálogo QX), y al
 * elegir uno su nombre y su nº de colegiado se guardan EN EL DOCUMENTO, no
 * solo se pintan en pantalla.
 *
 * Firmas del CIRUJANO (administración, 2026-09-17): debajo de la hoja, «Firma
 * anestesista» y al lado «Firma cirujano». El cirujano firma antes de lavarse
 * y al terminar, y cada firma SELLA la hora en el registro quirúrgico. La hora
 * la pone el servidor al firmar (la tablet puede ir con otra) y el fin no se
 * puede firmar hasta que firme el anestesista: aquí el botón sale apagado,
 * pero quien lo impide de verdad es el servidor. Es el MISMO recuadro de firma
 * (<FirmaDedo>) que el del anestesista, con ratón o con el dedo.
 *
 * «Modificada después de la firma» (23/9/2026, decisión de dirección): la hoja
 * firmada se sigue editando —se trabaja igual que antes: autoguardado tras
 * firmar, salida de quirófano después—, pero no engaña. Si se ha cambiado o
 * borrado algo que estaba escrito al firmar, debajo de la firma sale qué (lo
 * firmado → lo de ahora) y la última modificación. Lo calcula el SERVIDOR
 * (`modificada_tras_la_firma`) y viene en cada respuesta que devuelve la hoja:
 * aquí solo se pinta, sin decidir nada.
 *
 * Lo que se firma es lo que se ve (23/9/2026, 2ª pasada): antes de firmar se
 * guarda lo que hay en pantalla, y la firma va con esa versión (ver el botón
 * «Guardar firma»).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Check, Plus, Minus, PenLine } from 'lucide-react';
import { formatDate } from '../../lib/api';
import { upsertAnesthesiaRecord, saveAnesthesiaRecord } from '../../lib/citasApi';
import FirmaDedo from '../FirmaDedo';
import { api, mensajeError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { entradaDeUsuario, etiquetaColegiado } from '../../lib/medicos';
// ¿Lleva anestesia la intervención? Lo decide el servidor en vivo (D3,
// 23/9/2026); la pantalla suma lo que se acaba de poner en ella.
import { llevaAnestesiaEnPantalla } from '../../lib/anestesiaEnVivo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Filas de la gráfica (constantes + medicación) por columna de hora.
const GRAFICA_ROWS = [
  { key: 'o2', label: 'Ventilación O₂%' },
  { key: 'med1', label: 'Medicación' },
  { key: 'med2', label: '' },
  { key: 'med3', label: '' },
  { key: 'tas_tad', label: 'TAS/TAD' },
  { key: 'fc', label: 'FC' },
  { key: 'sat', label: 'Sat O₂%' },
  { key: 'otros', label: 'Otros' },
  { key: 'fluidos', label: 'Fluidos IV' },
];

const DEFAULT_COLS = 12;

// Casillas de la gráfica y de la salida de quirófano (administración, 24/9/2026:
// «la tabla no se ve… se ve muy clarito»). Son <input> sueltos, no el Input
// de la casa: sin fondo ni color propios se quedaban con el fondo BLANCO que
// el navegador pone a los campos y con el color de texto heredado del tema,
// y en el tema oscuro eso es texto casi blanco sobre blanco. Fondo
// transparente (el del diálogo, que sigue al tema) y el color de texto
// normal, nunca el gris atenuado. Y el contenido del diálogo lleva `min-w-0`:
// el diálogo es un grid y sin eso se ensanchaba a lo ancho de la tabla, así
// que en el móvil se desplazaba el diálogo entero y no la tabla en su caja.
const CASILLA = 'bg-transparent text-foreground font-medium border-0 focus:ring-1 focus:ring-[#2E6E5E]';

// Los dos momentos que firma el cirujano y qué sella cada uno en el registro
// quirúrgico (administración, 2026-09-17).
const MOMENTOS_CIRUJANO = [
  { clave: 'inicio', titulo: 'Inicio de cirugía', ayuda: 'Justo antes de lavarse: sella la hora de inicio.' },
  { clave: 'fin', titulo: 'Fin de cirugía', ayuda: 'Al terminar: sella la hora final.' },
];

function emptyForm(patientName) {
  return {
    nhc: '', nombre: patientName || '', edad: '', peso: '', sexo: '',
    ojo: '', diagnostico: '', alergias: '', asa: '', premedicacion: '',
    ttos: '', ap: '', preoperatorio: '',
    grafica: Array.from({ length: DEFAULT_COLS }, () => ({ hora: '' })),
    sedacion: '', a_topica: false, a_peri: false, a_gral: false,
    anestesico: '', vent_espontanea: false, vent_vmc: false, vent_iot: false,
    vias_g: '', vias_loc: '', incidencias: '',
    hora_inicio: '', hora_salida: '', cirujano: '',
    salida: { estado: '', ta: '', fc: '', sat: '', ventilacion: '', otros: '' },
  };
}

// Combina lo guardado con la estructura por defecto (por si faltan campos).
function mergeForm(patientName, saved) {
  const base = emptyForm(patientName);
  if (!saved || typeof saved !== 'object') return base;
  const grafica = Array.isArray(saved.grafica) && saved.grafica.length
    ? saved.grafica.map((c) => ({ hora: '', ...c }))
    : base.grafica;
  return {
    ...base,
    ...saved,
    grafica,
    salida: { ...base.salida, ...(saved.salida || {}) },
  };
}

// --- Subcomponentes de campo ---------------------------------------------

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-[11px] font-medium text-muted-foreground mb-0.5">{label}</label>
      {children}
    </div>
  );
}

function Txt({ value, onChange, className = '', ...rest }) {
  return (
    <Input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className={`h-8 text-sm ${className}`}
      {...rest}
    />
  );
}

function Chk({ label, checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
        checked
          ? 'bg-[#2E6E5E] border-[#2E6E5E] text-white'
          : 'bg-white border-[#D9E3DD] text-muted-foreground hover:border-[#2E6E5E]'
      }`}
    >
      <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center ${checked ? 'bg-white border-white' : 'border-slate-300'}`}>
        {checked && <Check className="w-3 h-3 text-[#2E6E5E]" />}
      </span>
      {label}
    </button>
  );
}

/** Lo que va escrito en el cuadro de firma de un documento (hoja o CI). */
function firmanteDe(doc) {
  return doc?.anesthetist_name
    ? { anesthetist_name: doc.anesthetist_name, anesthetist_license: doc.anesthetist_license || null }
    : null;
}

/**
 * Las firmas del cirujano de ESTE registro quirúrgico (remate 2026-09-17).
 *
 * La hoja de anestesia es ÚNICA por paciente y fecha, pero el mismo paciente
 * puede entrar dos veces a quirófano el mismo día. Si al abrir la cirugía de
 * la tarde se enseñara la firma de la mañana, nadie volvería a firmar y la
 * tarde se quedaría con la hora de la mañana: duración falsa e importe falso
 * en la factura del cirujano externo.
 *
 * El servidor las guarda por registro (`firmas_cirujano_por_registro`). Las
 * hojas firmadas antes del cambio solo tienen las sueltas: valen si son de
 * este registro (cada firma guarda su `surgery_log_id`).
 */
function firmasCirujanoDe(rec, surgeryLogId) {
  const propias = surgeryLogId && (rec?.firmas_cirujano_por_registro || {})[surgeryLogId];
  if (propias) return { inicio: propias.inicio || null, fin: propias.fin || null };
  const suya = (f) => (f && (!surgeryLogId || !f.surgery_log_id || f.surgery_log_id === surgeryLogId) ? f : null);
  return { inicio: suya(rec?.firma_cirujano_inicio), fin: suya(rec?.firma_cirujano_fin) };
}

/**
 * Quién sale elegido en el desplegable: el que ya consta guardado en el
 * documento y, si no consta ninguno, el anestesista que ha iniciado sesión
 * (si está en la lista).
 */
function quienFirma(doc, lista, nombreUsuario) {
  const fijado = doc?.anesthetist_catalog_id && (lista || []).find((e) => e.id === doc.anesthetist_catalog_id);
  return (fijado || entradaDeUsuario(nombreUsuario, lista))?.id || '';
}

/**
 * Desplegable de «quién firma»: los anestesistas dados de alta (Catálogo QX)
 * con su nº de colegiado al lado, y a continuación lo que queda escrito en el
 * cuadro de firma del documento.
 *
 * Es EL MISMO componente en la hoja de anestesia y en el consentimiento
 * (administración, 2026-09-09): misma lista, mismo texto y mismo formato, para que
 * los dos documentos se rellenen igual.
 */
function SelectorAnestesista({ anestesistas, value, onChange, guardando, firmante, base }) {
  return (
    <div className="my-2 flex items-center gap-2 flex-wrap" data-testid={`${base}-quien`}>
      <span className="text-xs text-muted-foreground">Anestesista que firma:</span>
      <div className="w-72">
        <Select value={value} onValueChange={onChange} disabled={guardando}>
          <SelectTrigger className="h-8 text-xs" data-testid={`${base}-anestesista`}>
            <SelectValue placeholder="Elige quién firma (nombre y colegiado)" />
          </SelectTrigger>
          <SelectContent>
            {(anestesistas || []).map((e) => (
              <SelectItem key={e.id} value={e.id}>{e.nombre} · {etiquetaColegiado(e.colegiado)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {firmante?.anesthetist_name && (
        <span className="text-xs text-[#13231E]" data-testid={`${base}-firmante`}>
          En el cuadro de firma: <strong>{firmante.anesthetist_name}</strong> · {etiquetaColegiado(firmante.anesthetist_license)}
        </span>
      )}
    </div>
  );
}

/**
 * «Modificada después de la firma» (23/9/2026, decisión de dirección): qué ha
 * cambiado de lo firmado (lo firmado → lo de ahora) y la última modificación.
 * Las frases de fecha, persona y hojas antiguas vienen hechas del servidor,
 * que es quien sabe qué consta y qué no: aquí no se inventa ninguna. Sin nada
 * cambiado no se pinta nada (un aviso en todas las hojas no lo lee nadie).
 */
function AvisoModificadaTrasLaFirma({ modificada }) {
  if (!modificada || !(modificada.campos || []).length) return null;
  return (
    <div
      className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
      role="status"
      data-testid="anest-modificada-tras-firma"
    >
      <p className="font-semibold text-red-700">Modificada después de la firma</p>
      <ul className="mt-1 space-y-0.5 list-disc pl-4">
        {modificada.campos.map((c) => (
          <li key={c.campo}>
            <span className="font-medium">{c.etiqueta}</span>: {c.firmado || 'vacío'} → {c.ahora || 'vacío'}
          </li>
        ))}
      </ul>
      {modificada.ultima_texto && <p className="mt-1">{modificada.ultima_texto}</p>}
      {modificada.quienes_texto && <p className="mt-0.5">{modificada.quienes_texto}</p>}
      {modificada.aviso_copia && <p className="mt-1 text-amber-800">{modificada.aviso_copia}</p>}
    </div>
  );
}

export default function AnesthesiaSheetDialog({
  open, onOpenChange, patient, surgeryDate, agendaId, citaId,
  // Registro quirúrgico donde se sellan las horas y cirujano de esa
  // intervención (2026-09-17). Si no vienen, el servidor busca el registro de
  // ese paciente y ese día y usa el cirujano que tenga apuntado.
  surgeryLogId = null, surgeon = '', onFirmas,
}) {
  const { user } = useAuth();
  const patientName = patient?.patient_name || patient?.name || '';
  const [loading, setLoading] = useState(false);
  const [recordId, setRecordId] = useState(null);
  // Firma del anestesista (dibujada con ratón o dedo).
  const [firma, setFirma] = useState(null); // {firma_b64, firmado_por_name, firmado_at}
  // Lo que el servidor dice que ha cambiado de lo firmado (o null).
  const [modificada, setModificada] = useState(null);
  const [firmando, setFirmando] = useState(false);
  const [guardandoFirma, setGuardandoFirma] = useState(false);
  const firmaRef = useRef(null);
  const [form, setForm] = useState(() => emptyForm(patientName));
  // CI de anestesia (2026-08-25): el anestesista lo firma desde aquí y queda
  // en la ficha del paciente, en consentimientos.
  const [ciAnestesia, setCiAnestesia] = useState(null);
  const [ciFirmando, setCiFirmando] = useState(false);
  // ¿Lleva anestesia la intervención? (23/9/2026, D3.) Lo dice el servidor al
  // abrir la hoja (`lleva_anestesia`), calculado en vivo con la MISMA regla
  // que el parte, la guía y la tablet. Mientras no diga que NO, el fin del
  // cirujano espera al anestesista, como siempre: solo un `false` explícito
  // (un chalazión con anestesia local) enciende el fin sin su firma.
  const [llevaAnestesia, setLlevaAnestesia] = useState(true);
  // …y si no la lleva porque el registro quirúrgico dice «¿Con sedación?» No
  // (administración, 8/10/2026). Entonces la firma del anestesista ni se pide
  // ni se ve (si ya estaba, se enseña en solo lectura), y lo que ya haya de
  // anestesia no vuelve a pedirla: para eso se cambia la sedación en el
  // registro (o se marca «Lleva anestesista»).
  const [sinSedacion, setSinSedacion] = useState(false);
  const [adjuntandoCi, setAdjuntandoCi] = useState(false);
  // Con la marca «Anestesia local, sin anestesista» el servidor ya no prepara
  // el CI de anestesia en blanco al abrir (D3, 23/9/2026): contesta sin `id`
  // y con el motivo, que se enseña en su sitio.
  const [ciNoSeCrea, setCiNoSeCrea] = useState(null);
  // Un error NUNCA se calla (administración, 24/9/2026: «el anestesista firma y no
  // se guarda, y no sale ningún mensaje»). Si el CI no se pudo cargar, o la
  // lista de anestesistas, se dice en su sitio: antes el recuadro desaparecía
  // sin más y «Guardar firma» pedía elegir en un desplegable vacío.
  const [ciFallo, setCiFallo] = useState(null);
  const [catalogoFallo, setCatalogoFallo] = useState(false);
  const [ciGuardando, setCiGuardando] = useState(false);
  const ciFirmaRef = useRef(null);
  // Quién firma el CI de anestesia (dirección, 2026-09-08, punto 7): un
  // desplegable con los anestesistas dados de alta (Catálogo QX); al elegirse,
  // su nombre y nº de colegiado pasan al cuadro de firma del consentimiento.
  // Por defecto, el anestesista que ha iniciado sesión (si está en la lista).
  const [anestesistas, setAnestesistas] = useState([]);
  const [anestesistaId, setAnestesistaId] = useState('');
  const [anestesistaGuardando, setAnestesistaGuardando] = useState(false);
  // Quién firma la HOJA (administración, 2026-09-09): el mismo desplegable y la
  // misma lista que el CI, pero guardado en la hoja (`anesthetist_name` y
  // `anesthetist_license` de `anesthesia_records`).
  const [hojaAnestesistaId, setHojaAnestesistaId] = useState('');
  const [hojaFirmante, setHojaFirmante] = useState(null);
  const [hojaAnestesistaGuardando, setHojaAnestesistaGuardando] = useState(false);
  // Firmas del CIRUJANO: la de inicio y la de fin, tal y como están guardadas
  // en la hoja (con la hora que selló el servidor).
  const [firmasCirujano, setFirmasCirujano] = useState({ inicio: null, fin: null });
  const [firmandoCirujano, setFirmandoCirujano] = useState(null); // 'inicio' | 'fin' | null
  const [guardandoCirujano, setGuardandoCirujano] = useState(false);
  const firmaCirujanoRef = useRef(null);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved

  const formRef = useRef(form);
  const recordIdRef = useRef(null);
  const timerRef = useRef(null);
  // El guardado que está en camino (promesa), para que salgan de uno en uno.
  const enCursoRef = useRef(null);
  formRef.current = form;
  recordIdRef.current = recordId;

  // Al abrir: obtener/crear la hoja del paciente para esta fecha.
  useEffect(() => {
    if (!open || !patient?.patient_id || !surgeryDate) return;
    let cancelled = false;
    setLoading(true);
    // La lista de anestesistas (nombre + colegiado) es la MISMA para el cuadro
    // de firma de la hoja y para el del consentimiento: se pide una sola vez y
    // no depende de que el CI se pueda crear (administración, 2026-09-09).
    setCatalogoFallo(false);
    const catalogo = api.get('/api/qx-catalogo', { params: { tipo: 'anestesista' } })
      .then((r) => (Array.isArray(r.data) ? r.data : []))
      .catch(() => {
        if (!cancelled) setCatalogoFallo(true);
        return [];
      });
    upsertAnesthesiaRecord({
      patientId: patient.patient_id, surgeryDate, citaId, agendaId,
      formData: {},
    })
      .then((rec) => {
        if (cancelled) return;
        setRecordId(rec.id);
        const initialForm = mergeForm(patientName, rec.form_data);
        // El procedimiento de la cita se precarga en el diagnóstico, pero NO
        // en una hoja firmada (23/9/2026): el guardado al cerrar lo mandaba
        // y cambiaba lo firmado solo por abrir la hoja desde el parte. Y
        // solo si el diagnóstico está en blanco (23/9/2026, 2ª pasada,
        // decisión de dirección): encima de lo escrito ponía lo de la cita
        // («Chalazión» sobre «Chalazión OI recidivante») sin que nadie lo
        // tocara, y se firmaba o se guardaba eso.
        const cerrada = !!(rec.firma_b64 || rec.firmado_at || rec.signed);
        const diagnosticoEscrito = String(initialForm.diagnostico || '').trim();
        if (patient?.procedure_text && !cerrada && !diagnosticoEscrito) {
          initialForm.diagnostico = patient.procedure_text;
        }
        setForm(initialForm);
        if (rec.template_used) {
          toast.info('Se ha precargado la última hoja de este anestesista como plantilla. Revisa los datos variables antes de guardar.', { duration: 5000 });
        }
        setFirma(rec.firma_b64 ? { firma_b64: rec.firma_b64, firmado_por_name: rec.firmado_por_name, firmado_at: rec.firmado_at } : null);
        setModificada(rec.modificada_tras_la_firma || null);
        setFirmando(false);
        setFirmasCirujano(firmasCirujanoDe(rec, surgeryLogId));
        setFirmandoCirujano(null);
        setHojaFirmante(firmanteDe(rec));
        setLlevaAnestesia(rec.lleva_anestesia !== false);
        setSinSedacion(rec.lleva_anestesia === false && rec.anestesia?.sin_sedacion === true);
        catalogo.then((lista) => {
          if (cancelled) return;
          setAnestesistas(lista);
          setHojaAnestesistaId(quienFirma(rec, lista, user?.name));
        });
        // El CI de anestesia del paciente, aparte de la hoja (best-effort).
        setCiFirmando(false);
        setCiNoSeCrea(null);
        setCiFallo(null);
        api.post('/api/consents/anestesia', { patient_id: patient.patient_id, surgery_date: surgeryDate })
          .then((r) => {
            if (cancelled) return;
            if (!r.data?.id) {
              setCiAnestesia(null);
              setCiNoSeCrea(r.data?.mensaje || null);
              return undefined;
            }
            setCiNoSeCrea(null);
            setCiAnestesia(r.data);
            return catalogo.then((lista) => {
              if (cancelled) return;
              setAnestesistaId(quienFirma(r.data, lista, user?.name));
            });
          })
          .catch((e) => {
            if (cancelled) return;
            setCiAnestesia(null);
            setCiFallo(mensajeError(e, 'sin respuesta del servidor'));
          });
      })
      .catch((e) => {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'No se pudo abrir la hoja de anestesia');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patient?.patient_id, surgeryDate]);

  /**
   * Guarda YA lo que hay en pantalla y devuelve la hoja guardada (o lanza el
   * error). Cancela el autoguardado pendiente, que ya va aquí dentro.
   *
   * Un guardado a la vez (23/9/2026, 2ª pasada): si hay otro en camino, este
   * sale detrás, con lo que haya en pantalla en ese momento. Si salieran a la
   * vez, el viejo podría llegar después y dejar guardado lo de antes, y la
   * firma iría sobre eso.
   */
  const guardarYa = useCallback(async () => {
    const id = recordIdRef.current;
    if (!id) return null;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    const previo = enCursoRef.current;
    const promesa = (async () => {
      if (previo) await previo.catch(() => {});
      return saveAnesthesiaRecord(id, formRef.current);
    })();
    enCursoRef.current = promesa;
    try {
      const guardada = await promesa;
      // Lo que el servidor dice DESPUÉS de este guardado: si se acaba de
      // cambiar algo de lo firmado, el aviso sale ya; si se ha vuelto a poner
      // lo firmado, se va.
      if (guardada && 'modificada_tras_la_firma' in guardada) {
        setModificada(guardada.modificada_tras_la_firma || null);
      }
      return guardada;
    } finally {
      if (enCursoRef.current === promesa) enCursoRef.current = null;
    }
  }, []);

  const doSave = useCallback(async () => {
    if (!recordIdRef.current) return;
    setSaveState('saving');
    try {
      await guardarYa();
      setSaveState('saved');
    } catch (e) {
      setSaveState('idle');
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    }
  }, [guardarYa]);

  // Autosave con debounce de 900 ms tras cada cambio.
  const scheduleSave = useCallback(() => {
    setSaveState('saving');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { doSave(); }, 900);
  }, [doSave]);

  // Flush al cerrar/desmontar para no perder lo último escrito. Detrás del
  // guardado que siga en camino, por lo mismo que `guardarYa`.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const id = recordIdRef.current;
    if (!id) return;
    const form = formRef.current;
    const previo = enCursoRef.current;
    if (previo) {
      previo.catch(() => {}).then(() => saveAnesthesiaRecord(id, form)).catch(() => {});
    } else {
      saveAnesthesiaRecord(id, form).catch(() => {});
    }
  }, []);

  // Quién firma la HOJA: se guarda EN LA HOJA al elegirlo, aunque todavía no
  // se haya dibujado la firma (administración, 2026-09-09).
  const elegirAnestesistaHoja = async (id) => {
    setHojaAnestesistaId(id);
    if (!recordId || !id) return;
    setHojaAnestesistaGuardando(true);
    try {
      const res = await api.put(`/api/anesthesia-records/${recordId}/anestesista`, { anestesista_id: id });
      setHojaFirmante(firmanteDe(res.data));
      // Un anestesista elegido en la hoja es anestesia en esta intervención
      // (D3, 23/9/2026): desde aquí el fin vuelve a esperar a su firma, como
      // hace el servidor.
      setLlevaAnestesia(true);
      // Cambiar quién firma DESPUÉS de firmar es cambiar lo firmado: el
      // servidor lo dice y aquí se pinta.
      if (res.data && 'modificada_tras_la_firma' in res.data) {
        setModificada(res.data.modificada_tras_la_firma || null);
      }
      const e = anestesistas.find((x) => x.id === id);
      toast.success(`${e?.nombre || 'Anestesista'} · ${etiquetaColegiado(e?.colegiado)} en el cuadro de firma de la hoja`);
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo fijar el anestesista'));
    } finally {
      setHojaAnestesistaGuardando(false);
    }
  };

  const elegirAnestesista = async (id) => {
    setAnestesistaId(id);
    if (!ciAnestesia?.id || !id) return;
    setAnestesistaGuardando(true);
    try {
      const res = await api.put(`/api/consents/${ciAnestesia.id}/anestesista`, { anestesista_id: id });
      setCiAnestesia(res.data);
      const e = anestesistas.find((x) => x.id === id);
      toast.success(`${e?.nombre || 'Anestesista'} · ${etiquetaColegiado(e?.colegiado)} en el cuadro de firma del consentimiento`);
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo fijar el anestesista'));
    } finally {
      setAnestesistaGuardando(false);
    }
  };

  /**
   * Adjuntar el CI de anestesia que estaba «en espera» (23/9/2026, D3: «el
   * anestesista adjunta el de sedación a mano»). Es elegirse en su cuadro de
   * firma: el mismo PUT que el desplegable de un CI normal. Desde ese momento
   * el servidor lo cuenta —la guía, el enlace y la tablet se lo piden a la
   * paciente y el parte lo exige— y aquí se pinta como cualquier otro.
   */
  const adjuntarCi = async () => {
    if (!ciAnestesia?.id) return;
    if (!anestesistaId) {
      toast.error('Elige en el desplegable qué anestesista lo adjunta');
      return;
    }
    setAdjuntandoCi(true);
    try {
      const res = await api.put(`/api/consents/${ciAnestesia.id}/anestesista`, { anestesista_id: anestesistaId });
      setCiAnestesia(res.data);
      setLlevaAnestesia(true);
      toast.success('Consentimiento de anestesia adjuntado: ya se le pide a la paciente');
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo adjuntar el consentimiento de anestesia'));
    } finally {
      setAdjuntandoCi(false);
    }
  };

  /**
   * Firma del cirujano: sella el inicio o el fin en el registro quirúrgico.
   *
   * La hora NO se manda desde aquí a propósito: la pone el servidor en el
   * momento de firmar. Tampoco se comprueba aquí si el anestesista ha firmado
   * —el botón sale apagado, pero la regla es del servidor—.
   */
  const firmarCirujano = async (momento) => {
    if (!firmaCirujanoRef.current || firmaCirujanoRef.current.isEmpty()) {
      toast.error('Dibuja la firma antes de guardar');
      return;
    }
    setGuardandoCirujano(true);
    try {
      const b64 = firmaCirujanoRef.current.getCanvas().toDataURL('image/png');
      const cuerpo = { momento, firma_b64: b64 };
      if (surgeon) cuerpo.cirujano = surgeon;
      if (surgeryLogId) cuerpo.surgery_log_id = surgeryLogId;
      const res = await api.put(`/api/anesthesia-records/${recordId}/firma-cirujano`, cuerpo);
      setFirmasCirujano(firmasCirujanoDe(res.data, surgeryLogId || res.data.registro_quirurgico?.id));
      setFirmandoCirujano(null);
      const registro = res.data.registro_quirurgico || {};
      const hora = momento === 'inicio' ? registro.hora_inicio : registro.hora_final;
      toast.success(momento === 'inicio'
        ? `Inicio de cirugía registrado${hora ? ` a las ${hora}` : ''}`
        : `Fin de cirugía registrado${hora ? ` a las ${hora}` : ''}`);
      onFirmas && onFirmas(res.data);
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo firmar'));
    } finally {
      setGuardandoCirujano(false);
    }
  };

  // Lo que dijo el servidor más lo que se acaba de hacer en pantalla y que
  // el servidor también cuenta (D3, 23/9/2026): firmar la hoja, elegir su
  // anestesista o adjuntar el CI hacen que la marca «local» deje de valer, el
  // fin vuelva a esperar al anestesista y el CI se pinte como uno que se pide.
  // Lo escrito en la hoja (sedación, anestésico) no decide nada. Con «sin
  // sedación» en el registro, nada de eso la vuelve a pedir (8/10/2026).
  const llevaAhora = llevaAnestesiaEnPantalla(llevaAnestesia, {
    firma, hojaFirmante, ci: ciAnestesia, sinSedacion,
  });

  const set = (key, value) => { setForm((f) => ({ ...f, [key]: value })); scheduleSave(); };
  const setSalida = (key, value) => { setForm((f) => ({ ...f, salida: { ...f.salida, [key]: value } })); scheduleSave(); };
  const setCol = (i, key, value) => {
    setForm((f) => {
      const grafica = f.grafica.slice();
      grafica[i] = { ...grafica[i], [key]: value };
      return { ...f, grafica };
    });
    scheduleSave();
  };
  const addCol = () => { setForm((f) => ({ ...f, grafica: [...f.grafica, { hora: '' }] })); scheduleSave(); };
  const removeCol = () => {
    setForm((f) => (f.grafica.length > 1 ? { ...f, grafica: f.grafica.slice(0, -1) } : f));
    scheduleSave();
  };

  const onClose = (o) => {
    if (!o) {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (recordIdRef.current) doSave();
    }
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[94vh] overflow-y-auto" data-testid="anesthesia-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 pr-6">
            <span>Hoja de anestesia · Registro de quirófano</span>
            <span className="text-xs font-normal text-muted-foreground inline-flex items-center gap-1.5">
              {saveState === 'saving' && (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Guardando…</>)}
              {saveState === 'saved' && (<><Check className="w-3.5 h-3.5 text-emerald-600" /> Guardado</>)}
            </span>
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {patientName}{surgeryDate ? ` · ${formatDate(surgeryDate)}` : ''}
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Abriendo hoja…
          </div>
        ) : (
          <div className="space-y-5 min-w-0">
            {sinSedacion && (
              <p
                className="rounded-md border border-border bg-[rgba(46,110,94,0.06)] px-3 py-2 text-xs text-[#13231E]"
                data-testid="anest-sin-sedacion"
              >
                Marcada sin sedación en el registro quirúrgico: no se piden la firma del anestesista ni
                la hoja de anestesia. Aquí firma el cirujano el inicio y el fin. Si hubo sedación,
                cámbialo en el registro («¿Con sedación?» Sí) o marca «Lleva anestesista».
              </p>
            )}
            {/* ---- Cabecera ---- */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              <Field label="Nº Hª Clínica"><Txt value={form.nhc} onChange={(v) => set('nhc', v)} /></Field>
              <Field label="Nombre" className="col-span-2"><Txt value={form.nombre} onChange={(v) => set('nombre', v)} /></Field>
              <Field label="Ojo"><Txt value={form.ojo} onChange={(v) => set('ojo', v)} placeholder="OD / OI / AO" /></Field>
              <Field label="Edad"><Txt value={form.edad} onChange={(v) => set('edad', v)} /></Field>
              <Field label="Peso (kg)"><Txt value={form.peso} onChange={(v) => set('peso', v)} /></Field>
              <Field label="Sexo">
                <div className="flex gap-1.5">
                  <Chk label="H" checked={form.sexo === 'H'} onChange={() => set('sexo', form.sexo === 'H' ? '' : 'H')} testid="anest-sexo-h" />
                  <Chk label="M" checked={form.sexo === 'M'} onChange={() => set('sexo', form.sexo === 'M' ? '' : 'M')} testid="anest-sexo-m" />
                </div>
              </Field>
              <Field label="ASA"><Txt value={form.asa} onChange={(v) => set('asa', v)} placeholder="I–V" /></Field>
              <Field label="Diagnóstico / Procedimiento" className="col-span-2 md:col-span-4"><Txt value={form.diagnostico} onChange={(v) => set('diagnostico', v)} /></Field>
              <Field label="Alergias" className="col-span-2"><Txt value={form.alergias} onChange={(v) => set('alergias', v)} /></Field>
              <Field label="Premedicación" className="col-span-2"><Txt value={form.premedicacion} onChange={(v) => set('premedicacion', v)} /></Field>
              <Field label="Ttos" className="col-span-2"><Txt value={form.ttos} onChange={(v) => set('ttos', v)} /></Field>
              <Field label="AP (antecedentes personales)" className="col-span-2 md:col-span-4"><Txt value={form.ap} onChange={(v) => set('ap', v)} /></Field>
              <Field label="Preoperatorio" className="col-span-2 md:col-span-4"><Txt value={form.preoperatorio} onChange={(v) => set('preoperatorio', v)} /></Field>
            </section>

            {/* ---- Gráfica por horas ---- */}
            <section>
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="text-sm font-semibold text-[#13231E]">Gráfica (por hora)</h3>
                <div className="flex items-center gap-1.5">
                  <Button type="button" size="sm" variant="outline" className="h-7 px-2" onClick={removeCol} title="Quitar columna"><Minus className="w-3.5 h-3.5" /></Button>
                  <Button type="button" size="sm" variant="outline" className="h-7 px-2" onClick={addCol} title="Añadir columna"><Plus className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div className="overflow-x-auto border border-[#D9E3DD] rounded-lg">
                <table className="border-collapse text-xs" data-testid="anest-grafica">
                  <thead>
                    <tr>
                      <th className="sticky left-0 z-10 bg-[#F4F8FC] border-b border-r border-[#D9E3DD] px-2 py-1 text-left font-medium text-muted-foreground min-w-[120px]">HORA</th>
                      {form.grafica.map((c, i) => (
                        <th key={i} className="border-b border-r border-[#D9E3DD] p-0.5 min-w-[64px]">
                          <input
                            value={c.hora || ''}
                            onChange={(e) => setCol(i, 'hora', e.target.value)}
                            placeholder="hh:mm"
                            className={`w-16 h-7 text-center tabular-nums text-xs rounded ${CASILLA}`}
                            data-testid={`anest-col-hora-${i}`}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {GRAFICA_ROWS.map((row) => (
                      <tr key={row.key}>
                        <td className="sticky left-0 z-10 bg-white border-b border-r border-[#D9E3DD] px-2 py-0.5 text-[#13231E] whitespace-nowrap">{row.label || ' '}</td>
                        {form.grafica.map((c, i) => (
                          <td key={i} className="border-b border-r border-[#EEF2F0] p-0">
                            <input
                              value={c[row.key] || ''}
                              onChange={(e) => setCol(i, row.key, e.target.value)}
                              className={`w-16 h-7 text-center text-xs ${CASILLA}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* ---- Anestesia ---- */}
            <section className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground mr-1">Sedación:</span>
                <Chk label="Sí" checked={form.sedacion === 'si'} onChange={() => set('sedacion', form.sedacion === 'si' ? '' : 'si')} testid="anest-sed-si" />
                <Chk label="No" checked={form.sedacion === 'no'} onChange={() => set('sedacion', form.sedacion === 'no' ? '' : 'no')} testid="anest-sed-no" />
                <span className="w-2" />
                <Chk label="A. Tópica" checked={form.a_topica} onChange={(v) => set('a_topica', v)} testid="anest-topica" />
                <Chk label="A. Peri/retrobulbar" checked={form.a_peri} onChange={(v) => set('a_peri', v)} testid="anest-peri" />
                <Chk label="A. General" checked={form.a_gral} onChange={(v) => set('a_gral', v)} testid="anest-gral" />
              </div>
              <Field label="Anestésico"><Txt value={form.anestesico} onChange={(v) => set('anestesico', v)} /></Field>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground mr-1">Ventilación:</span>
                <Chk label="Espontánea" checked={form.vent_espontanea} onChange={(v) => set('vent_espontanea', v)} testid="anest-vent-esp" />
                <Chk label="VMC" checked={form.vent_vmc} onChange={(v) => set('vent_vmc', v)} testid="anest-vent-vmc" />
                <Chk label="IOT" checked={form.vent_iot} onChange={(v) => set('vent_iot', v)} testid="anest-vent-iot" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                <Field label="Vías IV · NºG"><Txt value={form.vias_g} onChange={(v) => set('vias_g', v)} /></Field>
                <Field label="Localización" className="col-span-1 md:col-span-3"><Txt value={form.vias_loc} onChange={(v) => set('vias_loc', v)} /></Field>
              </div>
              <Field label="Incidencias"><Textarea value={form.incidencias} onChange={(e) => set('incidencias', e.target.value)} rows={2} className="text-sm" /></Field>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                <Field label="Hora inicio"><Txt value={form.hora_inicio} onChange={(v) => set('hora_inicio', v)} placeholder="hh:mm" /></Field>
                <Field label="Hora salida"><Txt value={form.hora_salida} onChange={(v) => set('hora_salida', v)} placeholder="hh:mm" /></Field>
                <Field label="Cirujano"><Txt value={form.cirujano} onChange={(v) => set('cirujano', v)} /></Field>
              </div>
            </section>

            {/* ---- Salida de quirófano ---- */}
            <section>
              <h3 className="text-sm font-semibold text-[#13231E] mb-1.5">Salida de quirófano</h3>
              <div className="overflow-x-auto border border-[#D9E3DD] rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      {['Estado', 'TA', 'FC', 'Sat O₂%', 'Ventilación', 'Otros'].map((h) => (
                        <th key={h} className="border-b border-r border-[#D9E3DD] px-2 py-1 font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {['estado', 'ta', 'fc', 'sat', 'ventilacion', 'otros'].map((k) => (
                        <td key={k} className="border-r border-[#EEF2F0] p-0">
                          <input
                            value={form.salida[k] || ''}
                            onChange={(e) => setSalida(k, e.target.value)}
                            className={`w-full min-w-[90px] h-8 px-2 text-xs ${CASILLA}`}
                            data-testid={`anest-salida-${k}`}
                          />
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Administración (2026-09-17): «debajo de la hoja de anestesia deben
                aparecer dos campos de firma: Firma anestesista y al lado Firma
                cirujano». Las dos con el dedo en la tablet o con el ratón. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">

            {/* Sin sedación (8/10/2026) el recuadro del anestesista no se
                enseña. Si la hoja ya estaba firmada (se marcó «Sí» y luego
                «No»), la firma se ve en solo lectura y se dice: no se borra. */}
            {sinSedacion && firma && (
              <section className="border border-border rounded-lg p-3" data-testid="anest-firma-solo-lectura">
                <span className="text-sm font-semibold text-[#13231E] inline-flex items-center gap-1.5">
                  <PenLine className="w-4 h-4 text-[#2E6E5E]" /> Firma del anestesista
                </span>
                <p className="text-xs text-amber-700 mt-1">
                  Se marcó sin sedación, pero la hoja tiene la firma del anestesista: no se pide, y se conserva.
                </p>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <img src={firma.firma_b64} alt="Firma" className="h-16 border border-border rounded bg-white" />
                  <span className="text-xs text-muted-foreground">
                    Firmada por {hojaFirmante?.anesthetist_name || firma.firmado_por_name}
                    {firma.firmado_at ? ` · ${formatDate((firma.firmado_at || '').slice(0, 10))}` : ''}
                  </span>
                </div>
              </section>
            )}

            {/* Firma del anestesista: con ratón en el ordenador o con el dedo
                en el móvil/tablet. Queda guardada en la hoja con autor y fecha. */}
            {!sinSedacion && (
            <section className="border border-border rounded-lg p-3" data-testid="anest-firma">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-sm font-semibold text-[#13231E] inline-flex items-center gap-1.5">
                  <PenLine className="w-4 h-4 text-[#2E6E5E]" /> Firma del anestesista
                </span>
                {firma && !firmando && (
                  <span className="text-xs text-muted-foreground" data-testid="anest-hoja-firmada-por">
                    Firmada por {hojaFirmante?.anesthetist_name || firma.firmado_por_name}
                    {hojaFirmante?.anesthetist_license ? ` · Colegiado ${hojaFirmante.anesthetist_license}` : ''}
                    {firma.firmado_at ? ` · ${formatDate((firma.firmado_at || '').slice(0, 10))}` : ''}
                  </span>
                )}
              </div>
              <SelectorAnestesista
                base="anest-hoja"
                anestesistas={anestesistas}
                value={hojaAnestesistaId}
                onChange={elegirAnestesistaHoja}
                guardando={hojaAnestesistaGuardando}
                firmante={hojaFirmante}
              />
              {firma && !firmando ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <img src={firma.firma_b64} alt="Firma" className="h-16 border border-border rounded bg-white" />
                  <Button type="button" size="sm" variant="outline" onClick={() => setFirmando(true)}>
                    Firmar de nuevo
                  </Button>
                </div>
              ) : (
                <div>
                  {/* Recuadro compartido: en el iPad, abrir el teclado o girar
                      disparaba `resize` y borraba la firma (revisión de
                      enlaces 2026-09-07). */}
                  <FirmaDedo ref={firmaRef} testid="anest-firma" alturaCls="h-36" penColor="#13231E" />
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      type="button"
                      size="sm"
                      className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                      disabled={guardandoFirma}
                      data-testid="anest-firmar"
                      onClick={async () => {
                        if (!firmaRef.current || firmaRef.current.isEmpty()) {
                          toast.error('Dibuja la firma antes de guardar');
                          return;
                        }
                        // Quién firma es obligatorio mientras haya lista que
                        // elegir; si el catálogo no ha podido cargarse, la
                        // firma no se bloquea (quirófano no puede quedarse sin
                        // poder firmar la hoja).
                        if (anestesistas.length && !hojaAnestesistaId) {
                          toast.error('Elige en el desplegable qué anestesista firma');
                          return;
                        }
                        setGuardandoFirma(true);
                        try {
                          const b64 = firmaRef.current.getCanvas().toDataURL('image/png');
                          // LO QUE SE FIRMA ES LO QUE SE VE (23/9/2026, 2ª
                          // pasada; decisión de dirección). El servidor firma
                          // lo GUARDADO: el procedimiento precargado y lo
                          // tecleado en los últimos 900 ms no había llegado,
                          // y el guardado al cerrar lo mandaba DESPUÉS encima
                          // de lo firmado («modificada después de la firma» a
                          // nombre de quien firmó). Se guarda antes lo de la
                          // pantalla; si no se puede, no se firma. La firma va
                          // con la versión recién guardada, y si otra pantalla
                          // la cambia entre medias el servidor no firma.
                          setSaveState('saving');
                          let guardada;
                          try {
                            guardada = await guardarYa();
                            setSaveState('saved');
                          } catch (e) {
                            setSaveState('idle');
                            toast.error(`No se ha firmado: antes hay que guardar lo que ves. ${mensajeError(e, 'No se pudo guardar la hoja.')}`);
                            return;
                          }
                          const cuerpo = { firma_b64: b64 };
                          if (hojaAnestesistaId) cuerpo.anestesista_id = hojaAnestesistaId;
                          if (guardada?.updated_at) cuerpo.updated_at = guardada.updated_at;
                          const res = await api.put(`/api/anesthesia-records/${recordId}/firma`, cuerpo);
                          setFirma({ firma_b64: res.data.firma_b64, firmado_por_name: res.data.firmado_por_name, firmado_at: res.data.firmado_at });
                          // Firmar de nuevo cubre lo que hay ahora: el aviso
                          // empieza de cero con la firma nueva.
                          setModificada(res.data.modificada_tras_la_firma || null);
                          setHojaFirmante(firmanteDe(res.data));
                          setFirmando(false);
                          toast.success('Hoja firmada');
                          // El panel «Firmas de quirófano» del registro vive
                          // fuera de esta hoja: si no se le avisa sigue
                          // diciendo que el anestesista no ha firmado cuando
                          // sí lo ha hecho (remate 2026-09-17).
                          onFirmas && onFirmas(res.data);
                        } catch (e) {
                          toast.error(mensajeError(e, 'No se pudo firmar'));
                        } finally {
                          setGuardandoFirma(false);
                        }
                      }}
                    >
                      {guardandoFirma ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Guardar firma'}
                    </Button>
                    {firma && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => setFirmando(false)}>Cancelar</Button>
                    )}
                  </div>
                </div>
              )}
              <AvisoModificadaTrasLaFirma modificada={modificada} />
            </section>
            )}

            {/* Firma del CIRUJANO (administración, 2026-09-17): sus dos firmas son
                las que sellan la hora de inicio y la de fin en el registro
                quirúrgico, para medir la duración real de cada cirugía. */}
            <section className="border border-border rounded-lg p-3" data-testid="cirujano-firmas">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm font-semibold text-[#13231E] inline-flex items-center gap-1.5">
                  <PenLine className="w-4 h-4 text-[#2E6E5E]" /> Firma del cirujano
                </span>
                {surgeon && (
                  <span className="text-xs text-[#13231E]" data-testid="cirujano-nombre">{surgeon}</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                La hora la pone el sistema en el momento de firmar: no hay que apuntarla a mano.
              </p>
              {MOMENTOS_CIRUJANO.map(({ clave, titulo, ayuda }) => {
                const firmada = firmasCirujano[clave];
                // El fin se firma DESPUÉS del anestesista. El botón apagado es
                // solo para no hacer perder el viaje; quien lo impide es el
                // servidor. Y solo si la intervención lleva anestesia (D3,
                // 23/9/2026): en un chalazión con anestesia local no firma
                // ningún anestesista y el fin se quedaba apagado para siempre.
                const bloqueada = clave === 'fin' && !firma && llevaAhora;
                const sinAnestesista = clave === 'fin' && !firma && !llevaAhora;
                return (
                  <div key={clave} className="border-t border-[#EEF3F0] pt-2 mt-2 first:border-t-0 first:pt-0 first:mt-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-[#13231E]">{titulo}</span>
                      {firmada ? (
                        <span className="text-xs text-emerald-700" data-testid={`cirujano-sellada-${clave}`}>
                          Firmada a las {firmada.hora}{firmada.cirujano ? ` · ${firmada.cirujano}` : ''}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">{ayuda}</span>
                      )}
                    </div>
                    {bloqueada && !firmada && (
                      <p className="text-xs text-amber-700 mt-1" data-testid="cirujano-fin-bloqueada">
                        Se firma después del anestesista: cuando firme la hoja (el despertar del paciente).
                      </p>
                    )}
                    {sinAnestesista && !firmada && (
                      <p className="text-xs text-muted-foreground mt-1" data-testid="cirujano-fin-sin-anestesia">
                        {sinSedacion
                          ? 'Marcada sin sedación en el registro quirúrgico: el fin no espera al anestesista.'
                          : 'Marcada «Anestesia local, sin anestesista» en el parte: el fin no espera al anestesista.'}
                      </p>
                    )}
                    {firmada && firmandoCirujano !== clave && (
                      <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <img src={firmada.firma_b64} alt={`Firma de ${titulo.toLowerCase()}`} className="h-14 border border-border rounded bg-white" />
                        <div className="flex flex-col gap-1">
                          <Button type="button" size="sm" variant="ghost" onClick={() => setFirmandoCirujano(clave)}>
                            <PenLine className="w-3.5 h-3.5 mr-1" /> Firmar de nuevo
                          </Button>
                          <span className="text-[11px] text-muted-foreground">
                            Volver a firmar no mueve la hora ya registrada; queda anotado.
                          </span>
                        </div>
                      </div>
                    )}
                    {!firmada && firmandoCirujano !== clave && (
                      <div className="mt-2">
                        <Button
                          type="button" size="sm" variant="outline" disabled={bloqueada}
                          data-testid={`cirujano-firmar-${clave}`}
                          onClick={() => setFirmandoCirujano(clave)}
                        >
                          <PenLine className="w-3.5 h-3.5 mr-1" /> Firmar {titulo.toLowerCase()}
                        </Button>
                      </div>
                    )}
                    {firmandoCirujano === clave && (
                      <div className="mt-2">
                        <FirmaDedo ref={firmaCirujanoRef} testid={`cirujano-firma-${clave}`} alturaCls="h-32" penColor="#13231E" />
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            type="button" size="sm" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                            disabled={guardandoCirujano}
                            data-testid={`cirujano-guardar-${clave}`}
                            onClick={() => firmarCirujano(clave)}
                          >
                            {guardandoCirujano ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Guardar firma'}
                          </Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => setFirmandoCirujano(null)}>Cancelar</Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </section>

            </div>

            {/* CI de ANESTESIA (2026-08-25, dirección): el anestesista lo firma
                desde quirófano y queda en la ficha del paciente, en
                consentimientos, junto a la firma del paciente. */}
            {/* CI de anestesia de una intervención marcada «Anestesia local,
                sin anestesista» (23/9/2026, DECISIÓN D3 de dirección): no se
                pinta como «falta» —nadie se lo pide a la paciente, y no se
                borra—, pero el anestesista puede adjuntarlo aquí si hace
                sedación, y entonces la marca deja de valer. */}
            {!ciAnestesia && ciNoSeCrea && (
              <p className="text-xs text-muted-foreground border border-dashed border-border rounded-lg p-3" data-testid="anest-ci-no-se-crea">
                {ciNoSeCrea}{' '}
                {sinSedacion
                  ? 'Si hubo sedación, cámbialo en el registro quirúrgico («¿Con sedación?» Sí) y vuelve a abrir la hoja.'
                  : 'Si hay sedación, quita esa marca en el parte (o marca «Lleva anestesista») y vuelve a abrir la hoja.'}
              </p>
            )}
            {!ciAnestesia && ciFallo && (
              <p className="text-xs text-red-700 border border-red-200 bg-red-50 rounded-lg p-3" role="alert" data-testid="anest-ci-fallo">
                No se ha podido cargar el consentimiento de anestesia ({ciFallo}), así que ahora no se puede firmar.
                Cierra la hoja y vuelve a abrirla; si sigue igual, avisa a dirección.
              </p>
            )}
            {/* Sin sedación (8/10/2026): no se le pide a la paciente y no hay
                «adjuntar» que valga —un anestesista en el CI no vuelve a
                pedir la anestesia—; se sale cambiando la sedación en el
                registro. Lo ya firmado se dice, y no se borra. */}
            {ciAnestesia && !llevaAhora && sinSedacion && (
              <section className="border border-dashed border-border rounded-lg p-3" data-testid="anest-ci-no-lleva">
                <div className="text-sm font-semibold text-[#13231E]">Consentimiento de anestesia</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Marcada sin sedación en el registro quirúrgico: no se le pide a la paciente.
                  Si hubo sedación, cámbialo en el registro («¿Con sedación?» Sí).
                </p>
                {(ciAnestesia.patient_signature_b64 || ciAnestesia.anesthetist_signature_b64) && (
                  <p className="text-xs text-amber-700 mt-1" data-testid="anest-ci-sin-sedacion-firmado">
                    Se marcó sin sedación, pero este consentimiento ya tiene la firma
                    {ciAnestesia.patient_signature_b64 && ciAnestesia.anesthetist_signature_b64
                      ? ' de la paciente y la del anestesista'
                      : ciAnestesia.patient_signature_b64 ? ' de la paciente' : ' del anestesista'}
                    : no se pide, y se conserva en su ficha.
                  </p>
                )}
              </section>
            )}
            {ciAnestesia && !llevaAhora && !sinSedacion && (
              <section className="border border-dashed border-border rounded-lg p-3" data-testid="anest-ci-no-lleva">
                <div className="text-sm font-semibold text-[#13231E]">Consentimiento de anestesia</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Marcada «Anestesia local, sin anestesista» en el parte: no se le pide a la paciente.
                  Si haces sedación, elige quién firma y adjúntalo; desde ese momento se le pide como cualquier otro.
                </p>
                <SelectorAnestesista
                  base="anest-ci"
                  anestesistas={anestesistas}
                  value={anestesistaId}
                  onChange={setAnestesistaId}
                  guardando={adjuntandoCi}
                  firmante={null}
                />
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={adjuntarCi}
                  disabled={adjuntandoCi}
                  data-testid="anest-ci-adjuntar"
                >
                  {adjuntandoCi ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Adjuntar consentimiento de anestesia (sedación)'}
                </Button>
              </section>
            )}
            {ciAnestesia && llevaAhora && (
              <section className="border border-border rounded-lg p-3" data-testid="anest-ci">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm font-semibold text-[#13231E]">Consentimiento de anestesia</div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className={ciAnestesia.patient_signature_b64 ? 'text-emerald-700' : 'text-amber-700'}>
                      {ciAnestesia.patient_signature_b64 ? '✓ Paciente firmado' : '○ Paciente sin firmar'}
                    </span>
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => window.open(`${BACKEND_URL}/api/consents/${ciAnestesia.id}/pdf`, '_blank', 'noopener')}
                      data-testid="anest-ci-pdf"
                    >
                      Ver PDF
                    </Button>
                  </div>
                </div>
                <SelectorAnestesista
                  base="anest-ci"
                  anestesistas={anestesistas}
                  value={anestesistaId}
                  onChange={elegirAnestesista}
                  guardando={anestesistaGuardando}
                  firmante={firmanteDe(ciAnestesia)}
                />
                {ciAnestesia.anesthetist_signature_b64 && !ciFirmando ? (
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <img src={ciAnestesia.anesthetist_signature_b64} alt="Firma anestesista" className="h-14 border border-border rounded bg-white" />
                    <div className="text-xs text-muted-foreground">
                      Firmado por {ciAnestesia.anesthetist_name || ciAnestesia.anesthetist_signed_by_name}
                      {ciAnestesia.anesthetist_license ? ` · Colegiado ${ciAnestesia.anesthetist_license}` : ''}
                      {ciAnestesia.anesthetist_signed_at ? ` · ${formatDate((ciAnestesia.anesthetist_signed_at || '').slice(0, 10))}` : ''}
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setCiFirmando(true)}>
                      <PenLine className="w-3.5 h-3.5 mr-1" /> Firmar de nuevo
                    </Button>
                  </div>
                ) : !ciFirmando ? (
                  <div className="mt-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => setCiFirmando(true)} data-testid="anest-ci-firmar">
                      <PenLine className="w-3.5 h-3.5 mr-1" /> Firmar como anestesista
                    </Button>
                  </div>
                ) : (
                  <div className="mt-2">
                    <FirmaDedo ref={ciFirmaRef} testid="anest-ci-firma" alturaCls="h-36" penColor="#13231E" />
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        type="button" size="sm"
                        className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                        disabled={ciGuardando}
                        data-testid="anest-ci-guardar"
                        onClick={async () => {
                          if (!ciFirmaRef.current || ciFirmaRef.current.isEmpty()) {
                            toast.error('Dibuja la firma antes de guardar');
                            return;
                          }
                          if (!anestesistaId) {
                            toast.error(catalogoFallo || !anestesistas.length
                              ? 'No se ha podido cargar la lista de anestesistas y sin elegir quién firma no se guarda la firma. Cierra la hoja y vuelve a abrirla; si sigue igual, avisa a dirección.'
                              : 'Elige en el desplegable qué anestesista firma');
                            return;
                          }
                          setCiGuardando(true);
                          try {
                            const b64 = ciFirmaRef.current.getCanvas().toDataURL('image/png');
                            const res = await api.put(`/api/consents/${ciAnestesia.id}/firma-anestesista`, { firma_b64: b64, anestesista_id: anestesistaId });
                            // Firmado es lo que el SERVIDOR devuelve firmado: una
                            // respuesta sin la firma no se pinta como guardada.
                            if (!String(res?.data?.anesthetist_signature_b64 || '').trim()) {
                              toast.error('La firma no ha quedado guardada en el consentimiento de anestesia. Tu firma sigue en el recuadro: vuelve a pulsar «Guardar firma»; si vuelve a pasar, avisa a dirección.');
                              return;
                            }
                            setCiAnestesia(res.data);
                            setCiFirmando(false);
                            toast.success('Consentimiento de anestesia firmado — queda en la ficha del paciente');
                          } catch (e) {
                            toast.error(`No se ha guardado tu firma en el consentimiento de anestesia: ${mensajeError(e, 'no hay respuesta del servidor')} Tu firma sigue en el recuadro: vuelve a pulsar «Guardar firma»; si vuelve a fallar, avisa a dirección.`);
                          } finally {
                            setCiGuardando(false);
                          }
                        }}
                      >
                        {ciGuardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Guardar firma'}
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setCiFirmando(false)}>Cancelar</Button>
                    </div>
                  </div>
                )}
              </section>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => onClose(false)}>Cerrar</Button>
              <Button type="button" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" onClick={doSave} data-testid="anest-guardar">
                Guardar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
